import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import WizardStateContext from 'components/Wizards/shared/context/wizardstate';
import { StyledWizardField } from 'components/Wizards/shared/styles';
import MultiSelect from 'components/Inputs/MultiSelect';
import KizenTypography from 'app/kizentypo';
import TeamMemberService from 'services/TeamMemberService';
import { BUSINESS, TEAM_MEMBERS } from 'queries/query-keys';
import {
  Entities,
  useSelectTypeaheadWithScroll,
} from 'components/Inputs/Select/hooks';
import { convertToTeamOption } from 'utility/TransformToSelectOptions';
import { REPORT_TYPES, AREA_RESPONSES } from 'components/Wizards/Dashlet/types';
import { FilterRow, AssigneeFiltersWrapper } from '../styles';
import { deferExecution } from 'utility/defer';

const UserRoleFilter = () => {
  const { t } = useTranslation();
  const { state, setState } = useContext(WizardStateContext.Context);
  const {
    reportType,
    includeRoles = [],
    includeEmployeesWithRoles = [],
    includeEmployees = [],
    area,
  } = state;

  const employeeSelectRef = useRef();

  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedEmployeesWithRoles, setSelectedEmployeesWithRoles] = useState(
    []
  );
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const shouldShow = useMemo(() => {
    return area === AREA_RESPONSES.ACTIVITIES;
  }, [area]);

  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: BUSINESS.ROLES,
    queryFn: () => TeamMemberService.getRolesOptionsList(),
    enabled: shouldShow,
    refetchOnMount: true,
  });

  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: TEAM_MEMBERS.GET(includeEmployees),
    queryFn: async () => {
      if (!includeEmployees.length) return [];
      const employees = await Promise.all(
        includeEmployees.map((id) => TeamMemberService.getById(id))
      );
      return employees;
    },
    enabled: shouldShow && includeEmployees.length > 0,
  });

  const setAssignedRoles = useCallback(
    (roles) => {
      setSelectedRoles(roles);
      setState(
        'includeRoles',
        roles.map(({ value }) => value)
      );
    },
    [setState]
  );

  const setAssignedEmployeesWithRoles = useCallback(
    (roles) => {
      setSelectedEmployeesWithRoles(roles);
      setState(
        'includeEmployeesWithRoles',
        roles.map(({ value }) => value)
      );
    },
    [setState]
  );

  const setAssignedEmployees = useCallback(
    (employees) => {
      setSelectedEmployees(employees);
      setState(
        'includeEmployees',
        employees.map(({ value }) => value)
      );
    },
    [setState]
  );

  const [employeeSelectProps] = useSelectTypeaheadWithScroll({
    selectRef: employeeSelectRef,
    objectToOption: convertToTeamOption,
    entity: Entities.TeamMember,
    chosenValueIds: includeEmployees,
  });

  const rebuildSelectedOptions = useCallback(
    (roles = [], employees = []) => {
      const validRoleIds = roles.map((role) => role.value);

      const selectedRoles = includeRoles
        .filter((id) => validRoleIds.includes(id))
        .map((id) => roles.find((role) => role.value === id))
        .filter(Boolean);

      const selectedEmployeesWithRoles = includeEmployeesWithRoles
        .filter((id) => validRoleIds.includes(id))
        .map((id) => roles.find((role) => role.value === id))
        .filter(Boolean);

      const selectedEmployeesOptions = employees?.map(
        ({ id, display_name }) => ({
          value: id,
          label: display_name,
        })
      );

      deferExecution(() => {
        setAssignedRoles(selectedRoles);
        setAssignedEmployeesWithRoles(selectedEmployeesWithRoles);
        setSelectedEmployees(selectedEmployeesOptions);
      });
    },
    [
      includeEmployeesWithRoles,
      includeRoles,
      setAssignedEmployeesWithRoles,
      setAssignedRoles,
    ]
  );

  const initialLoad = useRef(true);
  // Rebuild selected options when roles and employees load
  useEffect(() => {
    if (initialLoad.current && !rolesLoading && !employeesLoading) {
      rebuildSelectedOptions(
        rolesData,
        employeesData?.length > 0 ? employeesData : []
      );
      initialLoad.current = false;
    }
  }, [
    rolesData,
    employeesData,
    rebuildSelectedOptions,
    rolesLoading,
    employeesLoading,
  ]);

  if (!shouldShow) {
    return null;
  }

  return (
    <AssigneeFiltersWrapper
      maxWidth={
        reportType.value === REPORT_TYPES.SCHEDULED_ACTIVITIES
          ? 'calc(100% - 143px)'
          : ''
      }
    >
      <StyledWizardField flex>
        <KizenTypography type="subheader">
          {t('Assignee Filters')}
        </KizenTypography>
      </StyledWizardField>
      <FilterRow>
        <StyledWizardField flex top={12} fullWidth>
          <KizenTypography>
            {t('Assigned to Employee with Role')}
          </KizenTypography>

          <MultiSelect
            options={rolesData ?? []}
            isLoading={rolesLoading}
            placeholder={t('Choose Roles')}
            value={selectedEmployeesWithRoles}
            onChange={setAssignedEmployeesWithRoles}
            classNamePrefix="UserRoleFilter"
          />
        </StyledWizardField>
        {reportType.value === REPORT_TYPES.SCHEDULED_ACTIVITIES ||
        reportType.value === REPORT_TYPES.SCHEDULED_ACTIVITIES_CALENDAR ? (
          <StyledWizardField flex top={12} fullWidth>
            <KizenTypography>{t('Activity Assigned to Role')}</KizenTypography>
            <MultiSelect
              options={rolesData ?? []}
              isLoading={rolesLoading}
              placeholder={t('Choose Roles')}
              value={selectedRoles}
              onChange={setAssignedRoles}
              classNamePrefix="UserRoleFilter"
            />
          </StyledWizardField>
        ) : null}
        <StyledWizardField flex top={12} fullWidth>
          <KizenTypography>{t('Assigned to Employee')}</KizenTypography>
          <MultiSelect
            ref={employeeSelectRef}
            placeholder={t('Choose Team Members')}
            isLoading={employeesLoading}
            value={selectedEmployees}
            onChange={setAssignedEmployees}
            classNamePrefix="UserRoleFilter"
            {...employeeSelectProps}
          />
        </StyledWizardField>
      </FilterRow>
    </AssigneeFiltersWrapper>
  );
};

export default UserRoleFilter;
